/**
 * Create or edit one subscription plan, member or business.
 *
 * Mount it only while it is open (the page keys it per plan). The form is seeded once from the
 * plan in its initial state, so a background refetch of the list can't wipe what is being typed.
 *
 * Every field is saved — the API replaces the plan rather than patching it — which is what lets
 * "Unlimited" really clear a limit. The two AI narration fields are the exception on the API side
 * (null keeps the stored value), so they are always sent as a real boolean and number.
 *
 * Every setting has a (?) beside it with what the setting actually does in the app, written from
 * the code that enforces it (see planSpecs.ts).
 */

import { useState, type ReactNode } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import { plansApi } from '@api/endpoints/plans'
import type {
  AdminSubscriptionPlanDto,
  SaveBusinessSubscriptionPlanRequest,
  SaveSubscriptionPlanRequest,
} from '@api/types/plans'
import { FieldHelp } from './FieldHelp'
import {
  type AnyPlan,
  type FieldHelpInfo,
  type LimitField,
  type PlanDraft,
  type PlanKindSpec,
  QUICK_DESCRIPTION_MAX,
  blankDraft,
  draftFromPlan,
  planErrorMessage,
  planQueryKey,
  requestFromDraft,
  validateDraft,
} from './planSpecs'

export interface PlanEditorDialogProps {
  onClose: () => void
  spec: PlanKindSpec
  /** Null to create a new plan. */
  plan: AnyPlan | null
  /** Sort order a new plan starts with: after every existing one. */
  nextSortOrder: number
}

const twoColumns = { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } } as const

export function PlanEditorDialog({ onClose, spec, plan, nextSortOrder }: PlanEditorDialogProps) {
  const mobile = useMobileDialogProps()
  const queryClient = useQueryClient()
  const creating = plan === null
  const [draft, setDraft] = useState<PlanDraft>(() => (plan ? draftFromPlan(plan, spec) : blankDraft(spec, nextSortOrder)))
  // Field errors appear after the first save attempt, not while a new plan is still blank.
  const [attempted, setAttempted] = useState(false)

  const errors = validateDraft(draft, spec, creating)
  const shownErrors = attempted ? errors : {}
  const isValid = Object.keys(errors).length === 0

  const save = useMutation({
    mutationFn: (): Promise<AnyPlan> => {
      const body = requestFromDraft(draft, spec)
      if (spec.kind === 'consumer') {
        const request = body as unknown as SaveSubscriptionPlanRequest
        return plan ? plansApi.consumer.update(plan.id, request) : plansApi.consumer.create(request)
      }
      const request = body as unknown as SaveBusinessSubscriptionPlanRequest
      return plan ? plansApi.business.update(plan.id, request) : plansApi.business.create(request)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planQueryKey(spec.kind) })
      onClose()
    },
  })

  const submit = () => {
    setAttempted(true)
    if (isValid) save.mutate()
  }

  const set = <K extends keyof PlanDraft>(key: K, value: PlanDraft[K]) => setDraft((d) => ({ ...d, [key]: value }))
  const text = (key: keyof PlanDraft) => ({
    value: draft[key] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => set(key, e.target.value as never),
    error: Boolean(shownErrors[key]),
    fullWidth: true,
  })

  // The free plan is where every lapsed and cancelled subscription lands, so it has to stay free
  // and active. The server refuses it too; this just doesn't offer it.
  const lockedFreePlan = plan?.isFreeTier === true
  const subscribers = spec.kind === 'consumer' && plan ? (plan as AdminSubscriptionPlanDto).subscriberCount : 0
  const currencyAdornment = <InputAdornment position="start">{draft.currency.toUpperCase() || '—'}</InputAdornment>

  return (
    <Dialog open onClose={save.isPending ? undefined : onClose} maxWidth="md" fullWidth {...mobile}>
      <DialogTitle>{creating ? `New ${spec.noun}` : `Edit ${plan.displayName}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {subscribers > 0 && (
            <Alert severity="info">
              {subscribers.toLocaleString()} {subscribers === 1 ? 'person is' : 'people are'} on this plan. Saved
              changes apply to them straight away.
            </Alert>
          )}

          <Section title="Plan">
            <Box sx={twoColumns}>
              <WithHelp label="Display name" info={spec.help.displayName}>
                <TextField label="Display name" {...text('displayName')} autoFocus={creating} helperText={shownErrors.displayName ?? 'What people see.'} />
              </WithHelp>
              <WithHelp label="Name" info={spec.help.name}>
                <TextField
                  label="Name"
                  {...text('name')}
                  disabled={!creating}
                  helperText={
                    shownErrors.name ??
                    (creating
                      ? "Lowercase letters, numbers and dashes. Can't be changed later."
                      : 'Fixed once created: the app finds plans by it.')
                  }
                  inputProps={{ spellCheck: false, autoCapitalize: 'off', style: { fontFamily: 'monospace' } }}
                />
              </WithHelp>
            </Box>
            <WithHelp label="Short description" info={spec.help.quickDescription}>
              <TextField
                label="Short description"
                {...text('quickDescription')}
                helperText={shownErrors.quickDescription ?? `${draft.quickDescription.trim().length}/${QUICK_DESCRIPTION_MAX}`}
              />
            </WithHelp>
            <WithHelp label="Full description" info={spec.help.fullHtmlDescription}>
              <TextField
                label="Full description"
                {...text('fullHtmlDescription')}
                multiline
                minRows={3}
                helperText="Optional. HTML, for example a <ul> of what the plan includes."
                inputProps={{ spellCheck: false, style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
            </WithHelp>
          </Section>

          <Section title="Price">
            <Box sx={twoColumns}>
              <WithHelp label="Monthly price" info={spec.help.monthlyPrice}>
                <TextField
                  label="Monthly price"
                  {...text('monthlyPrice')}
                  type="number"
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{ startAdornment: currencyAdornment }}
                  helperText={shownErrors.monthlyPrice ?? ' '}
                />
              </WithHelp>
              <WithHelp label="Annual price" info={spec.help.annualPrice}>
                <TextField
                  label="Annual price"
                  {...text('annualPrice')}
                  type="number"
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{ startAdornment: currencyAdornment }}
                  helperText={shownErrors.annualPrice ?? 'Leave empty for no annual option.'}
                />
              </WithHelp>
              <WithHelp label="Currency" info={spec.help.currency}>
                <TextField
                  label="Currency"
                  {...text('currency')}
                  helperText={shownErrors.currency ?? 'Three-letter code.'}
                  inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                />
              </WithHelp>
              <WithHelp label="Trial" info={spec.help.trialPeriodDays}>
                <TextField
                  label="Trial"
                  {...text('trialPeriodDays')}
                  type="number"
                  inputProps={{ min: 0, max: 365 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                  helperText={shownErrors.trialPeriodDays ?? '0 for no trial.'}
                />
              </WithHelp>
            </Box>
          </Section>

          <Section title="Availability">
            <Box sx={twoColumns}>
              <SwitchField
                label="Active"
                checked={draft.isActive}
                disabled={lockedFreePlan}
                onChange={(v) => set('isActive', v)}
                info={spec.help.isActive}
                helper={
                  lockedFreePlan
                    ? 'The free plan always stays active.'
                    : 'Inactive plans are hidden from sign-up. Anyone already on one keeps it until it lapses.'
                }
              />
              <SwitchField
                label="Free plan"
                checked={draft.isFreeTier}
                disabled={lockedFreePlan}
                onChange={(v) => set('isFreeTier', v)}
                info={spec.help.isFreeTier}
                helper={
                  lockedFreePlan
                    ? 'This is the free plan. Lapsed and cancelled subscriptions fall back to it.'
                    : 'Where lapsed subscriptions land. There can only be one.'
                }
              />
              <WithHelp label="Sort order" info={spec.help.sortOrder}>
                <TextField
                  label="Sort order"
                  {...text('sortOrder')}
                  type="number"
                  helperText={shownErrors.sortOrder ?? 'Lower numbers are listed first.'}
                />
              </WithHelp>
            </Box>
          </Section>

          <Section title="Limits" caption="Unlimited means no cap. 0 means none at all.">
            <Box sx={twoColumns}>
              {spec.limits.map((limit) => (
                <LimitInput
                  key={limit.key}
                  field={limit}
                  value={draft.limits[limit.key] ?? null}
                  error={shownErrors[`limit:${limit.key}`]}
                  onChange={(value) => setDraft((d) => ({ ...d, limits: { ...d.limits, [limit.key]: value } }))}
                />
              ))}
            </Box>
          </Section>

          <Section title="Features">
            <Box sx={twoColumns}>
              {spec.features.map((feature) => (
                <SwitchField
                  key={feature.key}
                  label={feature.label}
                  helper={feature.helper}
                  info={feature.help}
                  checked={draft.features[feature.key] ?? false}
                  onChange={(value) =>
                    setDraft((d) => ({ ...d, features: { ...d.features, [feature.key]: value } }))
                  }
                />
              ))}
            </Box>
          </Section>

          {attempted && !isValid && <Alert severity="warning">Fix the highlighted fields to save.</Alert>}
          {save.error && <Alert severity="error">{planErrorMessage(save.error, 'The plan could not be saved')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : creating ? 'Create plan' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function Section({ title, caption, children }: { title: string; caption?: string; children: ReactNode }) {
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {title}
        </Typography>
        {caption && (
          <Typography variant="body2" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Box>
      {children}
    </Stack>
  )
}

/** A text field with its (?) to the right, lined up with the input rather than the helper text. */
function WithHelp({ label, info, children }: { label: string; info?: FieldHelpInfo; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="flex-start">
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      {info && (
        <Box sx={{ pt: 1.25 }}>
          <FieldHelp label={label} info={info} />
        </Box>
      )}
    </Stack>
  )
}

function SwitchField({
  label,
  helper,
  info,
  checked,
  disabled,
  onChange,
}: {
  label: string
  helper?: string
  info?: FieldHelpInfo
  checked: boolean
  disabled?: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <Box>
      <Stack direction="row" alignItems="center">
        <FormControlLabel
          control={<Switch checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />}
          label={label}
          sx={{ mr: 0 }}
        />
        <FieldHelp label={label} info={info} />
      </Stack>
      {helper && <FormHelperText sx={{ mt: -0.5, ml: 0 }}>{helper}</FormHelperText>}
    </Box>
  )
}

function LimitInput({
  field,
  value,
  error,
  onChange,
}: {
  field: LimitField
  value: string | null
  error?: string
  onChange: (value: string | null) => void
}) {
  // A limit without an Unlimited option is never null in the draft; guard anyway so it stays editable.
  const unlimited = value === null && !field.noUnlimited
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <TextField
        label={field.label}
        value={unlimited ? '' : (value ?? '')}
        placeholder={unlimited ? 'Unlimited' : undefined}
        disabled={unlimited}
        onChange={(e) => onChange(e.target.value)}
        type="number"
        size="small"
        fullWidth
        error={Boolean(error)}
        helperText={error ?? field.helper ?? ' '}
        inputProps={{ min: 0, step: field.unit === 'bytes' ? 'any' : 1 }}
        InputLabelProps={unlimited ? { shrink: true } : undefined}
        InputProps={{
          endAdornment: field.unit === 'bytes' ? <InputAdornment position="end">MB</InputAdornment> : undefined,
        }}
      />
      {!field.noUnlimited && (
        <FormControlLabel
          control={
            <Checkbox size="small" checked={unlimited} onChange={(e) => onChange(e.target.checked ? null : '0')} />
          }
          label="Unlimited"
          sx={{ whiteSpace: 'nowrap', mr: 0, mt: 0.25 }}
        />
      )}
      <Box sx={{ pt: 0.25 }}>
        <FieldHelp label={field.label} info={field.help} />
      </Box>
    </Stack>
  )
}
